import { TweenMax, Expo } from 'gsap';

/**
 * Opens an element
 *
 * @example
 * import {open} from '../helpers';
 *
 * open($('.element'));
 *
 * @param {Object} el - HTML element you want to target for animation
 * @param {Number} speed - Speed of the animation
 * @param {String} ease - Ease of the animation
 * @param {Function} onComplete - On complete function
 */
export const open = (el, speed = 0.4, ease = 'easeOut', onComplete) => {
  TweenMax.set(el, { height: 'auto' });
  TweenMax.from(el, speed, { height: 0, ease: Expo[ease], onComplete });
};

/**
 * Closes an element
 *
 * @example
 * import {close} from '../helpers';
 *
 * close($('.element'));
 *
 * @param {Object} el - HTML element you want to target for animation
 * @param {Number} speed - Speed of the animation
 * @param {String} ease - Ease of the animation
 * @param {Function} onComplete - On complete function
 */
export const close = (el, speed = 0.4, ease = 'easeOut', onComplete) => {
  TweenMax.to(el, speed, { height: 0, ease: Expo[ease], onComplete });
};

/**
* Scrolls to a certain part of the page with GSAP
*
* @param {Number} y - The new y position
* @param {Number} speed - The spped in which the screen scrolls
* @param {Function} onComplete - On complete function
*/
// export const scrollTo = (y, speed = 1.5, onComplete) => {
//   TweenMax.to(window, speed, { scrollTo: { y }, ease: Expo.easeOut, onComplete });
// };

/**
 * Renders React modules on a page
 *
 * @example
 * import {renderReactModule} from '../helpers';
 *
 * {name: 'bar/SomeComponent',
 *   render(component, el) {
 *     renderReactModule(component, el);
 *   }
 * }
 *
 * @param {Object} component - HTML element you want to target for animation
 * @param {Object} el - HTML element you want to target for animation
 * @param {Object} data - Additional data to be passed in to the React component
 */
/**
 * Fixes Choices.js accessibility issues with role="listbox" on the outer container.
 *
 * Choices.js v7 puts role="listbox" on both the outer container (div.choices)
 * and the inner dropdown list (div.choices__list). The outer container never
 * directly contains role="option" elements, violating ARIA rule SIA-R68.
 * The inner list already correctly has role="listbox" with the option elements,
 * so we remove the redundant role from the outer container.
 *
 * For dependent/empty dropdowns, also adds a disabled placeholder option so
 * the inner listbox is never empty.
 *
 * @param {Choices} choicesInstance - A Choices.js instance
 * @param {Object} [options] - Optional configuration
 * @param {string} [options.placeholderLabel] - If provided, adds a disabled placeholder option for empty dependent dropdowns
 */
export const fixChoicesListboxA11y = (choicesInstance, options = {}) => {
  if (!choicesInstance) return;

  // Choices returns early without initialising when the selector matches nothing.
  const containerOuter = choicesInstance.containerOuter;
  if (!containerOuter || !containerOuter.element) {
    return;
  }

  // Remove role="listbox" from the outer container (div.choices).
  // The inner div.choices__list already has role="listbox" with the actual options.
  const outerContainer = containerOuter.element;
  if (outerContainer) {
    outerContainer.removeAttribute('role');
  }

  // For dependent dropdowns that start empty, add a placeholder option
  // so the inner listbox always has at least one role="option" child.
  if (options.placeholderLabel) {
    choicesInstance.setChoices(
      [{ value: '', label: options.placeholderLabel, placeholder: true, disabled: true }],
      'value',
      'label',
      true
    );
  }
};

export const renderReactModule = (component, el, data) => {
  const length = el.length;
  const React = require('react');
  const ReactDom = require('react-dom');

  for (let i = 0; i < length; i++) {
    ReactDom.render(React.createElement(component, { ...data, ...el[i].dataset }), el[i]);
  }
};
